import { ApolloError } from '@apollo/client';
import dayjs from 'dayjs';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';
import { MAX_COUNTRY_CODES } from './countries';

/**
 * The contact list's state, its URL form, and the API variables built from it.
 * Everything here is pure so it can be tested without rendering.
 *
 * All of it lives in the URL's query string, so a contact opened from the list
 * returns to it exactly as it was left (crmReturnState), and a filtered list can
 * be bookmarked or pasted to a colleague.
 */

export type ConsentKey = 'canEmail' | 'canCall' | 'canText' | 'doNotCall' | 'doNotContact';

export const CONSENT_FILTERS: Array<{ key: ConsentKey; label: string }> = [
  { key: 'canEmail', label: 'Can email' },
  { key: 'canCall', label: 'Can call' },
  { key: 'canText', label: 'Can text' },
  { key: 'doNotCall', label: 'Do not call' },
  { key: 'doNotContact', label: 'Do not contact' },
];

export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = typeof PAGE_SIZES[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

/** Whole days, YYYY-MM-DD, both ends included. Either end may be left open. */
export interface DayRange {
  from?: string;
  to?: string;
}

/** Support level choices: one level, the two ends of the scale, or not set. */
export const SUPPORT_CHOICES: Array<{ value: string; label: string }> = [
  { value: '1-2', label: 'Supporters (1–2)' },
  { value: '1', label: '1 · Strong support' },
  { value: '2', label: '2 · Weak support' },
  { value: '3', label: '3 · Undecided' },
  { value: '4', label: '4 · Weak opposition' },
  { value: '5', label: '5 · Strong opposition' },
  { value: '4-5', label: 'Opposed (4–5)' },
  { value: 'none', label: 'Not set' },
];

export const SOURCES = ['NATIONBUILDER', 'APP', 'IMPORT', 'MANUAL', 'WORKFLOW'] as const;

export interface ContactListSort {
  column: string;
  direction: 'asc' | 'desc';
}

export interface ContactListState {
  /** filter.search: words, misspelt names, phone digits, exact addresses (§4). */
  search: string;
  /** Tags to have: any of them, or all of them. */
  tagIds: Array<number>;
  tagMatch: 'any' | 'all';
  /** Tags to not have. */
  excludeTagIds: Array<number>;
  /** Static lists to be on, any of them. */
  listIds: Array<number>;
  /** ISO 3166 alpha-2, upper case. */
  countries: Array<string>;
  /** "No country" chosen alongside them: `hasCountry` (card 18's API addition). */
  noCountry: boolean;
  /** "out" is `not: { countryCodes }`, which on its own also keeps contacts with no country. */
  countryMatch: 'in' | 'out';
  consent: Partial<Record<ConsentKey, boolean>>;
  /** When they first joined: NationBuilder sign-up, else the record (`joinedAt`, card 18). */
  joined: DayRange;
  /** When their app account was made (`accountCreatedAt`, card 18). */
  appJoined: DayRange;
  /** Has an app account, has none, or either. */
  appMember?: boolean;
  /** When they were last contacted, by any channel. */
  contacted: DayRange;
  /** Never contacted: wins over `contacted`. */
  neverContacted: boolean;
  /** A SUPPORT_CHOICES value, or null for any. */
  support: string | null;
  /** Point people (admin user ids). */
  ownerIds: Array<number>;
  /** "No point person": on its own, never with ownerIds. */
  noOwner: boolean;
  sources: Array<string>;
  stages: Array<string>;
  /** Null is the server's default: newest first, or best match with a search. */
  sort: ContactListSort | null;
  /** Zero-based. The URL shows it one-based. */
  page: number;
  pageSize: PageSize;
}

export const EMPTY_STATE: ContactListState = {
  search: '',
  tagIds: [],
  tagMatch: 'any',
  excludeTagIds: [],
  listIds: [],
  countries: [],
  noCountry: false,
  countryMatch: 'in',
  consent: {},
  joined: {},
  appJoined: {},
  appMember: undefined,
  contacted: {},
  neverContacted: false,
  support: null,
  ownerIds: [],
  noOwner: false,
  sources: [],
  stages: [],
  sort: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

/* ------------------------------------------------------------------ *
 * Columns
 * ------------------------------------------------------------------ */

export type CrmContactSortField =
  | 'NAME'
  | 'EMAIL'
  | 'PHONE'
  | 'COUNTRY'
  | 'REGION'
  | 'CITY'
  | 'CALL_COUNT'
  | 'LAST_CALLED_AT'
  | 'LAST_CONTACTED_AT'
  | 'SUPPORT_LEVEL'
  | 'LIFECYCLE_STAGE'
  | 'SOURCE'
  | 'ENGAGEMENT_RATE'
  | 'NB_CREATED_AT'
  | 'CREATED_AT'
  | 'JOINED_AT';

export interface ContactColumn {
  id: string;
  label: string;
  /** The API's sort, in order. Omitted for a column that cannot be sorted. */
  sortFields?: Array<CrmContactSortField>;
  /** Shown until the admin chooses otherwise. */
  initiallyShown: boolean;
  /** Cannot be hidden: it is the link to the contact. */
  required?: boolean;
  /** A related list, which costs the server one more query per page while shown. */
  needs?: 'tags' | 'owner' | 'engagement';
  /** How the column sorts, for "Sorted by …": [ascending, descending]. */
  orderWords?: [string, string];
}

export const COLUMNS: Array<ContactColumn> = [
  { id: 'name', label: 'Name', sortFields: ['NAME'], initiallyShown: true, required: true, orderWords: ['A–Z', 'Z–A'] },
  { id: 'email', label: 'Email', sortFields: ['EMAIL'], initiallyShown: true, orderWords: ['A–Z', 'Z–A'] },
  { id: 'phone', label: 'Phone', sortFields: ['PHONE'], initiallyShown: true, orderWords: ['ascending', 'descending'] },
  {
    id: 'location',
    label: 'Location',
    sortFields: ['COUNTRY', 'REGION', 'CITY'],
    initiallyShown: true,
    orderWords: ['A–Z by country, region, city', 'Z–A by country, region, city'],
  },
  { id: 'consent', label: 'Consent', initiallyShown: true },
  { id: 'tags', label: 'Tags', initiallyShown: true, needs: 'tags' },
  { id: 'calls', label: 'Calls', sortFields: ['CALL_COUNT'], initiallyShown: true, orderWords: ['fewest first', 'most first'] },
  {
    id: 'lastCalled',
    label: 'Last called',
    sortFields: ['LAST_CALLED_AT'],
    initiallyShown: true,
    orderWords: ['oldest first', 'newest first'],
  },
  {
    id: 'lastContacted',
    label: 'Last contacted',
    sortFields: ['LAST_CONTACTED_AT'],
    initiallyShown: true,
    orderWords: ['oldest first', 'newest first'],
  },
  {
    id: 'joined',
    label: 'Joined',
    sortFields: ['JOINED_AT'],
    initiallyShown: true,
    orderWords: ['earliest first', 'latest first'],
  },
  { id: 'owner', label: 'Point person', initiallyShown: false, needs: 'owner' },
  {
    id: 'support',
    label: 'Support level',
    sortFields: ['SUPPORT_LEVEL'],
    initiallyShown: false,
    orderWords: ['strongest support first', 'strongest opposition first'],
  },
  { id: 'stage', label: 'Stage', sortFields: ['LIFECYCLE_STAGE'], initiallyShown: false, orderWords: ['A–Z', 'Z–A'] },
  { id: 'source', label: 'Source', sortFields: ['SOURCE'], initiallyShown: false, orderWords: ['A–Z', 'Z–A'] },
  {
    id: 'engagement',
    label: 'Email opens',
    sortFields: ['ENGAGEMENT_RATE'],
    initiallyShown: false,
    needs: 'engagement',
    orderWords: ['lowest first', 'highest first'],
  },
  {
    id: 'memberSince',
    label: 'Joined NationBuilder',
    sortFields: ['NB_CREATED_AT'],
    initiallyShown: false,
    orderWords: ['oldest first', 'newest first'],
  },
  { id: 'added', label: 'Added to CRM', sortFields: ['CREATED_AT'], initiallyShown: false, orderWords: ['oldest first', 'newest first'] },
];

const COLUMN_BY_ID = new Map(COLUMNS.map((column) => [column.id, column]));

export const columnById = (id: string) => COLUMN_BY_ID.get(id);

export const INITIAL_COLUMNS: Array<string> = COLUMNS.filter((column) => column.initiallyShown).map((column) => column.id);

/** A stored or chosen set of columns, cleaned: known ids only, in table order, the name always in. */
export const normaliseColumns = (ids: ReadonlyArray<unknown>): Array<string> => {
  const wanted = new Set(ids.filter((id): id is string => typeof id === 'string'));

  return COLUMNS.filter((column) => column.required || wanted.has(column.id)).map((column) => column.id);
};

/* ------------------------------------------------------------------ *
 * URL <-> state
 * ------------------------------------------------------------------ */

/** The API's limits: 50 ids per tag or list set, 100 characters of search (card 12); 300 country codes. */
export const MAX_SET_IDS = 50;
export const MAX_SEARCH_LENGTH = 100;
const MAX_INT = 2147483647;

const parseIds = (value: string | null): Array<number> => {
  if (!value) return [];

  const ids = value
    .split(',')
    .filter((part) => /^[1-9]\d*$/.test(part))
    .map(Number)
    .filter((id) => id <= MAX_INT);

  return Array.from(new Set(ids)).slice(0, MAX_SET_IDS);
};

const parseCountries = (value: string | null): Array<string> => {
  if (!value) return [];

  const codes = value
    .split(',')
    .filter((part) => /^[A-Za-z]{2}$/.test(part))
    .map((part) => part.toUpperCase());

  return Array.from(new Set(codes)).slice(0, MAX_COUNTRY_CODES);
};

/** A real calendar day as YYYY-MM-DD, or undefined. */
const parseDay = (value: string | null): string | undefined => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d));
  return day.getUTCFullYear() === y && day.getUTCMonth() === m - 1 && day.getUTCDate() === d ? value : undefined;
};

const parseDayRange = (params: URLSearchParams, prefix: string): DayRange => {
  const range: DayRange = {};
  const from = parseDay(params.get(`${prefix}From`));
  const to = parseDay(params.get(`${prefix}To`));
  if (from) range.from = from;
  if (to) range.to = to;
  return range;
};

const writeDayRange = (params: URLSearchParams, prefix: string, range: DayRange) => {
  if (range.from) params.set(`${prefix}From`, range.from);
  if (range.to) params.set(`${prefix}To`, range.to);
};

/** "To" before "From": shown on the inputs, and the range is not sent. */
export const isBackwards = (range: DayRange) => Boolean(range.from && range.to && range.from > range.to);

const isOpen = (range: DayRange) => !range.from && !range.to;

/** The URL's token for "no country" among the codes: `country=US,none`. */
export const NO_COUNTRY = 'none';

const parseYesNo = (value: string | null): boolean | undefined => {
  if (value === 'yes') return true;
  if (value === 'no') return false;

  return undefined;
};

const parseSort = (value: string | null): ContactListSort | null => {
  if (!value) return null;

  const direction = value.startsWith('-') ? 'desc' : 'asc';
  const column = columnById(value.replace(/^-/, ''));

  return column?.sortFields ? { column: column.id, direction } : null;
};

/**
 * Reads the list's state from the URL. Anything malformed is dropped rather than
 * sent: a hand-edited link should show a wider list, not an error.
 */
export const parseListState = (params: URLSearchParams): ContactListState => {
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  const consent: ContactListState['consent'] = {};

  CONSENT_FILTERS.forEach(({ key }) => {
    const value = parseYesNo(params.get(key));
    if (value !== undefined) consent[key] = value;
  });

  const excludeTagIds = parseIds(params.get('notTags'));
  const excluded = new Set(excludeTagIds);

  const owners = params.get('owners');
  const support = params.get('support');

  return {
    search: (params.get('q') ?? '').trim().slice(0, MAX_SEARCH_LENGTH),
    // A tag both wanted and excluded matches nobody: excluding wins.
    tagIds: parseIds(params.get('tags')).filter((id) => !excluded.has(id)),
    tagMatch: params.get('tagMatch') === 'all' ? 'all' : 'any',
    excludeTagIds,
    listIds: parseIds(params.get('lists')),
    countries: parseCountries(params.get('country')),
    noCountry: (params.get('country') ?? '').split(',').includes(NO_COUNTRY),
    countryMatch: params.get('countryMatch') === 'out' ? 'out' : 'in',
    consent,
    joined: parseDayRange(params, 'joined'),
    appJoined: parseDayRange(params, 'app'),
    appMember: parseYesNo(params.get('app')),
    contacted: parseDayRange(params, 'contacted'),
    neverContacted: params.get('contacted') === 'never',
    support: SUPPORT_CHOICES.some((choice) => choice.value === support) ? support : null,
    ownerIds: owners === 'none' ? [] : parseIds(owners),
    noOwner: owners === 'none',
    sources: Array.from(new Set((params.get('source') ?? '').split(','))).filter((source) =>
      (SOURCES as ReadonlyArray<string>).includes(source)
    ),
    stages: Array.from(new Set(params.getAll('stage').map((stage) => stage.trim()).filter(Boolean)))
      .filter((stage) => stage.length <= 200)
      .slice(0, 100),
    sort: parseSort(params.get('sort')),
    page: Number.isInteger(page) && page > 1 ? page - 1 : 0,
    pageSize: (PAGE_SIZES as ReadonlyArray<number>).includes(size) ? (size as PageSize) : DEFAULT_PAGE_SIZE,
  };
};

/** The URL form of a state, leaving out every default so a plain list has a plain URL. */
export const toSearchParams = (state: ContactListState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.search) params.set('q', state.search);
  if (state.tagIds.length) params.set('tags', state.tagIds.join(','));
  if (state.tagIds.length > 1 && state.tagMatch === 'all') params.set('tagMatch', 'all');
  if (state.excludeTagIds.length) params.set('notTags', state.excludeTagIds.join(','));
  if (state.listIds.length) params.set('lists', state.listIds.join(','));
  const countries = [...state.countries, ...(state.noCountry ? [NO_COUNTRY] : [])];
  if (countries.length) params.set('country', countries.join(','));
  if (countries.length && state.countryMatch === 'out') params.set('countryMatch', 'out');

  CONSENT_FILTERS.forEach(({ key }) => {
    const value = state.consent[key];
    if (value !== undefined) params.set(key, value ? 'yes' : 'no');
  });

  writeDayRange(params, 'joined', state.joined);
  writeDayRange(params, 'app', state.appJoined);
  if (state.appMember !== undefined) params.set('app', state.appMember ? 'yes' : 'no');
  if (state.neverContacted) params.set('contacted', 'never');
  else writeDayRange(params, 'contacted', state.contacted);
  if (state.support) params.set('support', state.support);
  if (state.noOwner) params.set('owners', 'none');
  else if (state.ownerIds.length) params.set('owners', state.ownerIds.join(','));
  if (state.sources.length) params.set('source', state.sources.join(','));
  state.stages.forEach((stage) => params.append('stage', stage));

  if (state.sort) params.set('sort', `${state.sort.direction === 'desc' ? '-' : ''}${state.sort.column}`);
  if (state.page > 0) params.set('page', String(state.page + 1));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set('size', String(state.pageSize));

  return params;
};

/** Any change to what is listed starts again from the first page. */
export const withFilterChange = (state: ContactListState, change: Partial<ContactListState>): ContactListState => ({
  ...state,
  ...change,
  page: 0,
});

export const hasFilters = (state: ContactListState) =>
  Boolean(
    state.search ||
      state.tagIds.length ||
      state.excludeTagIds.length ||
      state.listIds.length ||
      state.countries.length ||
      state.noCountry ||
      Object.keys(state.consent).length ||
      moreFilterCount(state)
  );

/** How many of the "More filters" are set, for its heading. */
export const moreFilterCount = (state: ContactListState) =>
  [
    !isOpen(state.joined),
    !isOpen(state.appJoined),
    state.appMember !== undefined,
    state.neverContacted || !isOpen(state.contacted),
    Boolean(state.support),
    state.noOwner || state.ownerIds.length > 0,
    state.sources.length > 0,
    state.stages.length > 0,
  ].filter(Boolean).length;

/* ------------------------------------------------------------------ *
 * State -> API variables
 * ------------------------------------------------------------------ */

export interface CrmIdSetFilter {
  anyOf?: Array<number>;
  allOf?: Array<number>;
  noneOf?: Array<number>;
}

export interface CrmDateRange {
  from?: string;
  to?: string;
  isNull?: boolean;
}

export interface CrmContactFilter extends Partial<Record<ConsentKey, boolean>> {
  /** Specific contacts, at most 1,000: the rows ticked for a bulk change (card 19). */
  ids?: Array<number>;
  joinedAt?: CrmDateRange;
  accountCreatedAt?: CrmDateRange;
  hasAccount?: boolean;
  lastContactedAt?: CrmDateRange;
  supportLevel?: { min?: number; max?: number; isNull?: boolean };
  ownerAdminIds?: Array<number>;
  hasOwner?: boolean;
  sources?: Array<string>;
  lifecycleStages?: Array<string>;
  search?: string;
  tags?: CrmIdSetFilter;
  segments?: CrmIdSetFilter;
  countryCodes?: Array<string>;
  hasCountry?: boolean;
  or?: Array<CrmContactFilter>;
  not?: CrmContactFilter;
}

/** The start of a day, in UTC, as the API takes dates. */
const dayStart = (day: string) => `${day}T00:00:00.000Z`;

/** The start of the next day: the API's `to` excludes it, and the screen's "to" includes the day. */
const dayAfter = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString();
};

/** A day range as the API's date range; nothing when open or backwards. */
export const toDateRange = (range: DayRange): CrmDateRange | undefined => {
  if (isOpen(range) || isBackwards(range)) return undefined;
  return {
    ...(range.from ? { from: dayStart(range.from) } : {}),
    ...(range.to ? { to: dayAfter(range.to) } : {}),
  };
};

const supportRange = (choice: string | null): CrmContactFilter['supportLevel'] => {
  if (!choice) return undefined;
  if (choice === 'none') return { isNull: true };
  const [min, max]: Array<number> = choice.split('-').map(Number);
  return { min, max: max ?? min };
};

/** The `filter` argument, or undefined for everyone (the server leaves merged-away records out either way). */
export const buildFilter = (state: ContactListState): CrmContactFilter | undefined => {
  const filter: CrmContactFilter = {};

  if (state.search) filter.search = state.search;

  if (state.tagIds.length || state.excludeTagIds.length) {
    filter.tags = {};
    if (state.tagIds.length) filter.tags[state.tagMatch === 'all' ? 'allOf' : 'anyOf'] = state.tagIds;
    if (state.excludeTagIds.length) filter.tags.noneOf = state.excludeTagIds;
  }

  if (state.listIds.length) filter.segments = { anyOf: state.listIds };

  const codes = state.countries;
  if (state.countryMatch === 'out') {
    // Outside these countries; leaving out "no country" too when it is chosen.
    if (codes.length) filter.not = { countryCodes: codes };
    if (state.noCountry) filter.hasCountry = true;
  } else if (codes.length && state.noCountry) {
    filter.or = [{ countryCodes: codes }, { hasCountry: false }];
  } else if (codes.length) {
    filter.countryCodes = codes;
  } else if (state.noCountry) {
    filter.hasCountry = false;
  }

  CONSENT_FILTERS.forEach(({ key }) => {
    const value = state.consent[key];
    if (value !== undefined) filter[key] = value;
  });

  const joined = toDateRange(state.joined);
  if (joined) filter.joinedAt = joined;
  const appJoined = toDateRange(state.appJoined);
  if (appJoined) filter.accountCreatedAt = appJoined;
  if (state.appMember !== undefined) filter.hasAccount = state.appMember;
  if (state.neverContacted) filter.lastContactedAt = { isNull: true };
  else {
    const contacted = toDateRange(state.contacted);
    if (contacted) filter.lastContactedAt = contacted;
  }
  const support = supportRange(state.support);
  if (support) filter.supportLevel = support;
  if (state.noOwner) filter.hasOwner = false;
  else if (state.ownerIds.length) filter.ownerAdminIds = state.ownerIds;
  if (state.sources.length) filter.sources = state.sources;
  if (state.stages.length) filter.lifecycleStages = state.stages;

  return Object.keys(filter).length ? filter : undefined;
};

export interface CrmContactSort {
  field: CrmContactSortField;
  direction: 'ASC' | 'DESC';
}

/** The `sort` argument, or undefined for the server's default. */
export const buildSort = (state: ContactListState): Array<CrmContactSort> | undefined => {
  const fields = state.sort ? columnById(state.sort.column)?.sortFields : undefined;

  if (!state.sort || !fields) return undefined;

  const direction = state.sort.direction === 'desc' ? 'DESC' : 'ASC';

  return fields.map((field) => ({ field, direction }));
};

/** What a click on a column's header does: ascending, then descending, then the default order. */
export const nextSort = (current: ContactListSort | null, columnId: string): ContactListSort | null => {
  if (current?.column !== columnId) return { column: columnId, direction: 'asc' };
  if (current.direction === 'asc') return { column: columnId, direction: 'desc' };

  return null;
};

/** The order in words, for the line above the table. */
export const describeSort = (state: ContactListState): string => {
  const column = state.sort ? columnById(state.sort.column) : undefined;

  if (state.sort && column?.orderWords) {
    return `Sorted by ${column.label.toLowerCase()}, ${column.orderWords[state.sort.direction === 'asc' ? 0 : 1]}`;
  }

  return state.search ? 'Best matches first' : 'Newest first';
};

/* ------------------------------------------------------------------ *
 * Display
 * ------------------------------------------------------------------ */

export interface CrmContactTotal {
  count: number;
  exact: boolean;
}

/**
 * The row count in words. Above 10,000 the API estimates (§7): shown as "about",
 * rounded to the hundred so it does not look exact.
 */
export const describeTotal = (total: CrmContactTotal): string => {
  const noun = total.count === 1 ? 'contact' : 'contacts';

  if (total.exact) return `${total.count.toLocaleString('en-US')} ${noun}`;

  return `About ${(Math.round(total.count / 100) * 100).toLocaleString('en-US')} ${noun}`;
};

/** "51–100" for the rows on screen. */
export const describeRows = (page: number, pageSize: number, rowsOnPage: number) =>
  rowsOnPage ? `${(page * pageSize + 1).toLocaleString('en-US')}–${(page * pageSize + rowsOnPage).toLocaleString('en-US')}` : '0';

/** The search reasons worth saying: the ones not visible in the row itself (§4). */
const MATCH_HINTS: Record<string, string> = {
  SIMILAR_NAME: 'Similar name',
  OTHER_EMAIL: 'Matched another email',
  OTHER_PHONE: 'Matched another number',
};

export const matchHints = (matchedOn: ReadonlyArray<string> | null | undefined): Array<string> =>
  (matchedOn ?? []).map((reason) => MATCH_HINTS[reason]).filter((hint): hint is string => Boolean(hint));

/** NationBuilder's scale. */
const SUPPORT_LEVELS: Record<number, string> = {
  1: 'Strong support',
  2: 'Weak support',
  3: 'Undecided',
  4: 'Weak opposition',
  5: 'Strong opposition',
};

export const describeSupportLevel = (level: number | null | undefined) =>
  level == null ? '' : `${level} · ${SUPPORT_LEVELS[level] ?? 'Unknown'}`;

let countryNames: Intl.DisplayNames | null | undefined;

/** "Nigeria" for "NG"; the code itself where the browser cannot name it. */
export const countryName = (code: string): string => {
  if (countryNames === undefined) {
    try {
      countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      countryNames = null;
    }
  }

  try {
    return countryNames?.of(code) ?? code;
  } catch {
    return code;
  }
};

export const formatDate = (value: string | null | undefined) => (value ? dayjs(value).format('D MMM YYYY') : '');

export const formatRate = (rate: number | null | undefined) => (rate == null ? '' : `${Math.round(rate * 100)}%`);

/* ------------------------------------------------------------------ *
 * Errors (§15)
 * ------------------------------------------------------------------ */

export const errorCodeOf = (error: ApolloError | undefined) =>
  error?.graphQLErrors?.[0]?.extensions?.code as string | undefined;

export const errorFieldOf = (error: ApolloError | undefined) =>
  error?.graphQLErrors?.[0]?.extensions?.field as string | undefined;

/** What the list says when its request fails. Messages are shown only for codes the panel knows. */
export const describeListError = (error: ApolloError): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Could not load contacts');

  const first = error.graphQLErrors?.[0];

  switch (errorCodeOf(error)) {
    case 'CRM_INVALID_QUERY':
      return first?.message ?? 'The API refused this filter.';
    case 'CRM_QUERY_TIMEOUT':
      return 'The list took too long and was stopped. Narrow the search or the filters and try again.';
    case 'CRM_UNAVAILABLE':
      return 'The CRM database is not available on this server.';
    case 'FORBIDDEN':
      return 'Only admins can see the CRM.';
    default:
      return error.networkError ? 'Could not reach the API. Check the connection and try again.' : 'Could not load contacts.';
  }
};
